"use client";

import {
  useInView,
  animate,
  useMotionValue,
  useTransform,
  motion,
  useScroll,
  useSpring,
} from "framer-motion";
import Image from "next/image";
import React, { useRef, useState, useEffect } from "react";

const Produktivitas = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });
  const [scaleRange, setScaleRange] = useState([1, 1.3]);

  useEffect(() => {
    const updateScaleRange = () => {
      if (window.innerWidth < 768) {
        setScaleRange([1, 2]); // mobile
      } else {
        setScaleRange([1, 1.3]); // desktop
      }
    };

    updateScaleRange(); // run initially
    window.addEventListener("resize", updateScaleRange);

    return () => {
      window.removeEventListener("resize", updateScaleRange);
    };
  }, []);

  const bgPosY = useTransform(scrollYProgress, [0, 1], ["30%", "100%"]);
  const smoothBgPosY = useSpring(bgPosY, { stiffness: 100, damping: 30 });

  const bgScale = useTransform(scrollYProgress, [0, 1], scaleRange);
  const smoothBgScale = useSpring(bgScale, { stiffness: 100, damping: 30 });

  function Number({ n, decimals = 2, duration = 2.5 }) {
    const numberRef = useRef(null);
    const isInView = useInView(numberRef, { once: true });

    const count = useMotionValue(0);
    const [display, setDisplay] = useState("0");

    const rounded = useTransform(count, (latest) => latest.toFixed(decimals));

    useEffect(() => {
      if (isInView) {
        const controls = animate(count, n, {
          duration,
          ease: [0, 0, 0.75, 1],
        });

        const unsubscribe = rounded.on("change", (v) => {
          setDisplay(v);
        });

        return () => {
          controls.stop();
          unsubscribe();
        };
      }
    }, [isInView, n, duration, decimals, count, rounded]);

    return (
      <span ref={numberRef} className="text-3xl sm:text-4xl md:text-6xl">
        {display}
      </span>
    );
  }

  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current) {
        const scrollPosition = window.scrollY;
        containerRef.current.style.backgroundPositionY = `-${
          scrollPosition * 0.1
        }px`;
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <div className="flex flex-col justify-center items-center px-5 sm:px-10 md:px-16 gap-5 md:gap-7 overflow-hidden">
      <motion.div
        className="w-full text-3xl font-bold text-cust-blue z-10 text-center md:text-left"
        initial={{ x: 50, opacity: 0 }}
        whileInView={{
          x: 0,
          opacity: 1,
        }}
        transition={{
          duration: 0.5,
          delay: 1,
          ease: "easeInOut",
        }}
        viewport={{ once: true }}
      >
        Produktivitas{" "}
        <span className="text-cust-orange">UMKM dan Kewirausahaan</span>
      </motion.div>
      <motion.div
        ref={containerRef}
        className="relative flex flex-col-reverse md:flex-row justify-between items-end w-full h-full rounded-4xl overflow-hidden px-7 sm:px-12 md:px-6 lg:px-20 gap-7 sm:gap-12 md:gap-20"
        initial={{ opacity: 0 }}
        whileInView={{
          opacity: 1,
        }}
        transition={{
          duration: 0.5,
          delay: 1.25,
          ease: "easeInOut",
        }}
        viewport={{ once: true }}
      >
        <motion.div
          style={{
            backgroundImage: "url('/image/bg-produktivitas.webp')",
            backgroundPositionY: smoothBgPosY,
            scale: smoothBgScale,
          }}
          className="absolute inset-0 bg-cover bg-bottom bg-no-repeat z-0"
        />
        <div className="absolute inset-0 bg-cust-blue/60 z-10" />
        <motion.div
          className="relative flex justify-end items-end w-full md:w-1/2 h-64 md:h-64 lg:h-80 xl:h-96 pt-0 md:pt-5 z-20"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{
            duration: 0.5,
            delay: 1.75,
            ease: "easeInOut",
          }}
          viewport={{ once: true }}
        >
          <Image
            src="/image/penjual.webp"
            alt="image"
            fill
            sizes="(min-width: 768px) 50vw, 100vw"
            className="object-contain"
          />
        </motion.div>

        <div className="flex flex-col w-full md:w-1/2 text-white pt-10 pb-0 md:py-10 gap-0 z-20">
          <div className="flex flex-row w-full justify-start items-center gap-7">
            <motion.div
              className="text-xl font-medium"
              initial={{ x: 50, opacity: 0 }}
              whileInView={{
                x: 0,
                opacity: 1,
              }}
              transition={{
                duration: 0.5,
                delay: 2,
                ease: "easeInOut",
              }}
              viewport={{ once: true }}
            >
              Sasaran Utama
            </motion.div>
            <motion.hr
              className="bg-white w-32 h-1 rounded-full"
              initial={{ x: 50, opacity: 0 }}
              whileInView={{
                x: 0,
                opacity: 1,
              }}
              transition={{
                duration: 0.5,
                delay: 2.25,
                ease: "easeInOut",
              }}
              viewport={{ once: true }}
            />
          </div>
          <motion.div
            className="text-4xl md:text-6xl font-medium leading-tight"
            initial={{ x: 50, opacity: 0 }}
            whileInView={{
              x: 0,
              opacity: 1,
            }}
            transition={{
              duration: 0.5,
              delay: 2.5,
              ease: "easeInOut",
            }}
            viewport={{ once: true }}
          >
            Meningkatnya Lapangan Kerja yang Berkualitas.
          </motion.div>
        </div>
      </motion.div>
      <div className="flex flex-col md:flex-row justify-center items-center w-full gap-5">
        <div className="flex flex-col justify-start items-center w-full gap-5">
          <motion.div
            className="w-full font-bold text-2xl text-cust-blue"
            initial={{ x: 50, opacity: 0 }}
            whileInView={{
              x: 0,
              opacity: 1,
            }}
            transition={{
              duration: 0.5,
              delay: 1.5,
              ease: "easeInOut",
            }}
            viewport={{ once: true }}
          >
            Proporsi Jumlah Usaha Mikro, Kecil, dan Menengah
          </motion.div>
          <div className="grid grid-cols-3 w-full gap-5">
            <motion.div
              className="flex flex-col justify-center items-center w-full bg-[#428DD21A] rounded-2xl p-2 sm:p-3 md:p-5 gap-2 sm:gap-3 md:gap-4 shadow-none hover:shadow-xl transition-shadow duration-300 ease-in-out"
              initial={{ x: -50, opacity: 0 }}
              whileInView={{
                x: 0,
                opacity: 1,
              }}
              transition={{
                duration: 0.5,
                delay: 2,
                ease: "easeInOut",
              }}
              viewport={{ once: true }}
            >
              <div className="flex justify-center text-black font-medium w-full">
                <Number n={3.06} />
              </div>
              <div className="text-[#5C5D5F] text-sm md:text-lg leading-tight w-full">
                Baseline <br />
                tahun 2024
              </div>
            </motion.div>
            <motion.div
              className="flex flex-col justify-center items-center w-full bg-[#428DD21A] rounded-2xl p-2 sm:p-3 md:p-5 gap-2 sm:gap-3 md:gap-4 shadow-none hover:shadow-xl transition-shadow duration-300 ease-in-out"
              initial={{ y: 50, opacity: 0 }}
              whileInView={{
                y: 0,
                opacity: 1,
              }}
              transition={{
                duration: 0.5,
                delay: 2,
                ease: "easeInOut",
              }}
              viewport={{ once: true }}
            >
              <div className="flex justify-center text-black font-medium w-full">
                <Number n={3.1} />
              </div>
              <div className="text-[#5C5D5F] text-sm md:text-lg leading-tight w-full">
                Target (%)
                <br />
                tahun 2025
              </div>
            </motion.div>
            <motion.div
              className="flex flex-col justify-center items-center w-full bg-[#428DD21A] rounded-2xl p-2 sm:p-3 md:p-5 gap-2 sm:gap-3 md:gap-4 shadow-none hover:shadow-xl transition-shadow duration-300 ease-in-out"
              initial={{ x: 50, opacity: 0 }}
              whileInView={{
                x: 0,
                opacity: 1,
              }}
              transition={{
                duration: 0.5,
                delay: 2,
                ease: "easeInOut",
              }}
              viewport={{ once: true }}
            >
              <div className="flex justify-center text-black font-medium w-full">
                <Number n={3.3} />
              </div>
              <div className="text-[#5C5D5F] text-sm md:text-lg leading-tight w-full">
                Target (%)
                <br />
                tahun 2029
              </div>
            </motion.div>
          </div>
        </div>
        <div className="flex flex-col justify-start items-center w-full gap-5">
          <motion.div
            className="w-full font-bold text-2xl text-cust-blue"
            initial={{ x: 50, opacity: 0 }}
            whileInView={{
              x: 0,
              opacity: 1,
            }}
            transition={{
              duration: 0.5,
              delay: 1.5,
              ease: "easeInOut",
            }}
            viewport={{ once: true }}
          >
            Rasio Kewirausahaan
          </motion.div>
          <div className="grid grid-cols-3 w-full gap-5">
            <motion.div
              className="flex flex-col justify-center items-center w-full bg-[#EFA90829] rounded-2xl p-2 sm:p-3 md:p-5 gap-2 sm:gap-3 md:gap-4 shadow-none hover:shadow-xl transition-shadow duration-300 ease-in-out"
              initial={{ x: -50, opacity: 0 }}
              whileInView={{
                x: 0,
                opacity: 1,
              }}
              transition={{
                duration: 0.5,
                delay: 2,
                ease: "easeInOut",
              }}
              viewport={{ once: true }}
            >
              <div className="flex justify-center text-black font-medium w-full">
                <Number n={3.08} />
              </div>
              <div className="text-[#5C5D5F] text-sm md:text-lg leading-tight w-full">
                Baseline <br />
                tahun 2024
              </div>
            </motion.div>
            <motion.div
              className="flex flex-col justify-center items-center w-full bg-[#EFA90829] rounded-2xl p-2 sm:p-3 md:p-5 gap-2 sm:gap-3 md:gap-4 shadow-none hover:shadow-xl transition-shadow duration-300 ease-in-out"
              initial={{ y: 50, opacity: 0 }}
              whileInView={{
                y: 0,
                opacity: 1,
              }}
              transition={{
                duration: 0.5,
                delay: 2,
                ease: "easeInOut",
              }}
              viewport={{ once: true }}
            >
              <div className="flex justify-center text-black font-medium w-full">
                <Number n={3.14} />
              </div>
              <div className="text-[#5C5D5F] text-sm md:text-lg leading-tight w-full">
                Target (%)
                <br />
                tahun 2025
              </div>
            </motion.div>
            <motion.div
              className="flex flex-col justify-center items-center w-full bg-[#EFA90829] rounded-2xl p-2 sm:p-3 md:p-5 gap-2 sm:gap-3 md:gap-4 shadow-none hover:shadow-xl transition-shadow duration-300 ease-in-out"
              initial={{ x: 50, opacity: 0 }}
              whileInView={{
                x: 0,
                opacity: 1,
              }}
              transition={{
                duration: 0.5,
                delay: 2,
                ease: "easeInOut",
              }}
              viewport={{ once: true }}
            >
              <div className="flex justify-center text-black font-medium w-full">
                <Number n={3.6} />
              </div>
              <div className="text-[#5C5D5F] text-sm md:text-lg leading-tight w-full">
                Target (%)
                <br />
                tahun 2029
              </div>
            </motion.div>
          </div>
        </div>
      </div>
      <motion.div
        className="text-[#5C5D5F] font-normal text-base w-full"
        initial={{
          opacity: 0,
        }}
        whileInView={{
          opacity: 1,
        }}
        transition={{
          duration: 1,
          ease: "easeInOut",
          delay: 1.5,
        }}
        viewport={{ once: true }}
      >
        Catatan: <br />
        <ol className="list-disc pl-6 flex flex-col">
          <li>Rancangan RPJMN 2025 - 2029, Kementerian PPN/Bappenas</li>
          <li>Sumberdata SAKERNAS Tahun 2024: 56.2011.395 UMKM</li>
        </ol>
      </motion.div>
    </div>
  );
};

export default Produktivitas;
